import React from 'react';
import useUndoConfirmStore from '../../stores/useUndoConfirmStore';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/dialog';

/**
 * Shown when an Undo would also discard tools applied after the one being undone.
 *
 * The tools are named rather than counted: "Smoothing and Carve Relief will also
 * be undone" is actionable, "2 later changes will be lost" is not.
 */
export default function UndoTruncationDialog() {
  const pending = useUndoConfirmStore((state) => state.pending);
  const clearPending = useUndoConfirmStore((state) => state.clearPending);

  const names = pending?.alsoUndoneNames ?? [];
  const list =
    names.length <= 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

  const cancel = () => {
    pending?.onCancel?.();
    clearPending();
  };

  const confirm = () => {
    const { onConfirm } = pending ?? {};
    clearPending();
    onConfirm?.();
  };

  return (
    <AlertDialog
      open={!!pending}
      onOpenChange={(open) => {
        if (!open) cancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Undo {pending?.toolName ?? 'this tool'}?</AlertDialogTitle>
          <AlertDialogDescription>
            {names.length > 0 ? (
              <>
                {list} {names.length === 1 ? 'was' : 'were'} applied after it, on the
                same socket, so {names.length === 1 ? 'it' : 'they'} will be undone
                too. You will need to set {names.length === 1 ? 'it' : 'them'} up
                again.
              </>
            ) : (
              <>This will restore the socket to how it was before the tool was applied.</>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep changes</AlertDialogCancel>
          <AlertDialogAction onClick={confirm}>
            Undo {names.length > 0 ? 'all of them' : 'it'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
